// Types
import AddOnService from '@spike/addon-service-model';
import Service from 'model/Service';
import { EnhancedProduct } from './useDiscountForm';

/**
 * useServiceCategoryConfigs - Service Category Selector Configuration Hook
 *
 * Purpose:
 * Generates configuration objects for rendering ServiceCategorySelector buttons
 * in the "Select Services" section of the discount form.
 *
 * Responsibilities:
 * - Creates configuration for each service category selector button
 * - Determines visibility based on business configuration and data availability
 * - Provides selected item counts for each category
 * - Supplies appropriate labels and empty state messages
 * - Connects category buttons to their respective drawer open handlers
 *
 * Service Categories Configured:
 * 1. Grooming Services
 * 2. Daycare Services
 * 3. Boarding Services
 * 4. Dental Services, goes with grooming
 * 5. Transportation Services
 * 6. Evaluation Services
 * 7. Add-ons
 * 8. Products
 *
 * Key Features:
 * - Dynamic visibility: Only shows categories enabled in business configuration
 * - Data-driven display: Hides categories with no available items
 * - Real-time counts: Shows number of selected items per category
 * - Standardized configuration: Uniform structure for all category selectors
 *
 * Configuration Structure:
 * Each config includes:
 * - id: Unique identifier for the category
 * - show: Whether to display this category button
 * - label: Display name for the category
 * - selectedCount: Number of items currently selected
 * - emptyMessage: Message shown when no items are available
 * - onOpen: Handler to open the corresponding selection drawer
 *
 * When to use:
 * Use this hook to generate the list of service category buttons displayed
 * in the discount form. It handles all the logic for what to show and how to
 * configure each button based on business settings and available data.
 *
 * @param {UseServiceCategoryConfigsParams} params - All service data, selections, and drawer handlers
 * @returns {ServiceCategoryConfig[]} Array of configuration objects for each service category
 */

interface ServiceCategoryConfig {
    id: string;
    show: boolean;
    label: string;
    selectedCount: number;
    emptyMessage: string;
    onOpen: () => void;
}

interface ItemSelection<T> {
    openDrawer: () => void;
}

interface UseServiceCategoryConfigsParams {
    hasGrooming: boolean;
    hasDaycare: boolean;
    hasBoarding: boolean;
    hasTransportation: boolean;
    hasEvaluation: boolean;
    groomingServices: Service[];
    daycareServices: Service[];
    boardingServices: Service[];
    transportationServices: Service[];
    evaluationServices: Service[];
    addons: AddOnService[];
    products: EnhancedProduct[];
    selectedGroomingServices: Service[];
    selectedDaycareServices: Service[];
    selectedBoardingServices: Service[];
    selectedTransportationServices: Service[];
    selectedEvaluationServices: Service[];
    selectedAddons: AddOnService[];
    selectedProducts: EnhancedProduct[];
    groomingSelection: ItemSelection<Service>;
    daycareSelection: ItemSelection<Service>;
    boardingSelection: ItemSelection<Service>;
    transportationSelection: ItemSelection<Service>;
    evaluationSelection: ItemSelection<Service>;
    addonsSelection: ItemSelection<AddOnService>;
    productsSelection: ItemSelection<EnhancedProduct>;
}

interface UseServiceCategoryConfigsReturn {
    configs: ServiceCategoryConfig[];
    emptyServices: boolean;
}

export const useServiceCategoryConfigs = (params: UseServiceCategoryConfigsParams): UseServiceCategoryConfigsReturn => {
    const {
        hasGrooming,
        hasDaycare,
        hasBoarding,
        hasTransportation,
        hasEvaluation,
        groomingServices,
        daycareServices,
        boardingServices,
        transportationServices,
        evaluationServices,
        addons,
        products,
        selectedGroomingServices,
        selectedDaycareServices,
        selectedBoardingServices,
        selectedTransportationServices,
        selectedEvaluationServices,
        selectedAddons,
        selectedProducts,
        groomingSelection,
        daycareSelection,
        boardingSelection,
        transportationSelection,
        evaluationSelection,
        addonsSelection,
        productsSelection
    } = params;

    const configs: ServiceCategoryConfig[] = [
        {
            id: 'grooming',
            show: hasGrooming && groomingServices.length > 0,
            label: 'Grooming services',
            selectedCount: selectedGroomingServices.length,
            emptyMessage: 'No Grooming Services',
            onOpen: groomingSelection.openDrawer
        },
        {
            id: 'daycare',
            show: hasDaycare && daycareServices.length > 0,
            label: 'Daycare services',
            selectedCount: selectedDaycareServices.length,
            emptyMessage: 'No Daycare Services',
            onOpen: daycareSelection.openDrawer
        },
        {
            id: 'boarding',
            show: hasBoarding && boardingServices.length > 0,
            label: 'Boarding services',
            selectedCount: selectedBoardingServices.length,
            emptyMessage: 'No Boarding Services',
            onOpen: boardingSelection.openDrawer
        },
        {
            id: 'transportation',
            show: hasTransportation && transportationServices.length > 0,
            label: 'Transportation services',
            selectedCount: selectedTransportationServices.length,
            emptyMessage: 'No Transportation Services',
            onOpen: transportationSelection.openDrawer
        },
        {
            id: 'evaluation',
            show: hasEvaluation && evaluationServices.length > 0,
            label: 'Evaluation services',
            selectedCount: selectedEvaluationServices.length,
            emptyMessage: 'No Evaluation Services',
            onOpen: evaluationSelection.openDrawer
        },
        {
            id: 'addons',
            show: addons.length > 0,
            label: 'Add-ons',
            selectedCount: selectedAddons.length,
            emptyMessage: 'No Add-ons',
            onOpen: addonsSelection.openDrawer
        },
        {
            id: 'products',
            show: products.length > 0,
            label: 'Products',
            selectedCount: selectedProducts.length,
            emptyMessage: 'No Products',
            onOpen: productsSelection.openDrawer
        }
    ];

    // Check if all show flags are false (meaning no services are available)
    const emptyServices = configs.every(config => !config.show);

    return {
        configs,
        emptyServices
    };
};
